/**
 * Core-whiskey gap detection.
 *
 * HY wants every account to carry both a Rye and a Bourbon. Accounts
 * missing either are bumped to the top of Today's Plan and the
 * Follow-Up Tracker so reps pitch the core line first.
 *
 * "Bourbon" here means the Bottled in Bond SKU specifically (per the
 * business definition) — not the aged American Whiskey line. Product
 * short names arrive brand-prefix-stripped in `FollowUpAccount.products`
 * ("Rye", "Bottled in B" — the bourbon name is stored truncated). We
 * match by case-insensitive prefix so the truncation, or a future
 * full-name fix, doesn't silently break the check.
 */

function hasProductPrefix(products: string[], prefix: string): boolean {
  const p = prefix.toLowerCase();
  return products.some((name) => name.toLowerCase().startsWith(p));
}

/** True when the account carries the Rye SKU. */
function hasRye(products: string[]): boolean {
  return hasProductPrefix(products, 'rye');
}

/** True when the account carries the Bourbon (Bottled in Bond) SKU. */
function hasBourbon(products: string[]): boolean {
  return hasProductPrefix(products, 'bottled in b');
}

/**
 * Which of the two core SKUs the account is missing, for UI labels.
 * Returns any of ['Rye', 'Bourbon'] — empty when the account carries
 * both.
 */
export function missingCoreWhiskey(products: string[] | null | undefined): string[] {
  const list = products ?? [];
  const missing: string[] = [];
  if (!hasRye(list)) missing.push('Rye');
  if (!hasBourbon(list)) missing.push('Bourbon');
  return missing;
}

/**
 * True when the account is missing a Rye, a Bourbon, or both — the
 * signal that flags it to the top of the ranked lists.
 */
export function lacksCoreWhiskey(products: string[] | null | undefined): boolean {
  return missingCoreWhiskey(products).length > 0;
}
